import { B2Tabs } from "@hdb/biome-2";
import { useQuery } from "@tanstack/react-query";
import React, { CSSProperties, FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getEnergyConsumptionDataByBlockCode } from "src/core/api/services/dashbaorad-details/energy-consumption.service";
import {
  AssetDisplayLevelChartType,
  EnergyConsumptionChartType,
} from "src/core/enums/dashboard-enums";
import CustomHighchartComponent from "src/shared/custom-highchart-component/custom-highchart.component";
import EnergyStackedChartComponent from "src/shared/custom-highchart-component/energy-stacked-chart.component";
import { generateFilenameWithTimestamp } from "src/shared/helpers/generateFilenameWithTimestamp";
import { getassetDisplayLevelAndLocationsByBlock } from "src/store/slices/dashboard.slice";
import CustomSplineChart from "../../../../../shared/custom-highchart-component/custom-spline-chart.component";
import CustomSpinnerComponent from "../../../../../shared/custom-spinner/custom-spinner.component";
import TabularFilterComponent from "../extra-components/tabular.filter.component";
import ChipComponent from "src/core/components/chip/chip.component";

interface EnergyConsumptionChartTypes {
  blockCode: any;
  isRefreshed: boolean;
  blockDetails: any;
}

const EnergyConsumptionChart: FC<EnergyConsumptionChartTypes> = ({
  blockCode,
  isRefreshed,
  blockDetails,
}) => {
  const dispatch = useDispatch();

  const [energyConsumptionChartData, setEnergyConsumptionChartData] = useState<any>(
    []
  );
  const [savedEnergy, setSavedEnergy] = useState<number[]>([]);

  const [energyConsumptionBaselineValues, setEnergyConsumptionBaselineValues] =
    useState<any>(null);

  const [assetDisplayLevelsByBlock, setAssetDisplayLevelsByBlock] =
    useState<any>(null);

  const [
    switchEnergyConsumptionChartView,
    setSwitchEnergyConsumptionChartView,
  ] = useState<number>(EnergyConsumptionChartType.Energy_Vs_Time);

  const [energyConsumptionVsLevelData, setEnergyConsumptionVsLevelData] =
    useState<{
      seriesData: any[];
      xAxisValues: any[];
    }>({ seriesData: [], xAxisValues: [] });

  const [energyConsumptionVsLocationData, setEnergyConsumptionVsLocationData] =
    useState<{
      seriesData: [];
      xAxisValues: [];
    }>({ seriesData: [], xAxisValues: [] });

  const [energyConsumptionVsSensorData, setEnergyConsumptionVsSensorData] =
    useState<{
      energyConsumed: [];
      sensorNode: [];
      extraData: [];
    }>({
      energyConsumed: [],
      sensorNode: [],
      extraData: [],
    });

  const [activeKey, setActiveKey] = useState("Time");
  const [render, setRender] = useState(0);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Responsive placeholder for tabular filter
  const [responsivePlaceholder, setResponsivePlaceholder] = useState(
    window.innerWidth <= 992 ? "Select" : "Select Levels/Locations"
  );

  // Responsive style for tabular filter
  const [tabularFilterStyle, setTabularFilterStyle] = useState<
    CSSProperties | undefined
  >(undefined);

  useEffect(() => {
    dispatch(
      getassetDisplayLevelAndLocationsByBlock({
        blockCode,
        chartType: AssetDisplayLevelChartType.Energy_Consumption_Sensor,
      })
    ).then((response: any) => {
      setAssetDisplayLevelsByBlock(response?.payload);
    });
  }, [blockCode]);

  // Update placeholder and style based on window width
  useEffect(() => {
    const updatePlaceholder = () => {
      if (window.innerWidth <= 992) {
        setResponsivePlaceholder("Select");
        setTabularFilterStyle({
          width: "15%",
          position: "absolute",
          zIndex: "999",
          top: "-50px",
          right: "0",
        });
      } else {
        setResponsivePlaceholder("Select Levels/Locations");
        setTabularFilterStyle({
          width: "22%",
          position: "absolute",
          zIndex: "999",
          top: "-50px",
          right: "0",
        });
      }
    };

    // Set initial placeholder
    updatePlaceholder();

    // Add event listener for window resize
    window.addEventListener("resize", updatePlaceholder);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", updatePlaceholder);
    };
  }, []);

  const {
    data: energyConsumptionData,
    isLoading: isLoadingEnergyConsumptionData,
  } = useQuery({
    queryKey: [
      "getEnergyConsumptionData",
      blockCode,
      switchEnergyConsumptionChartView,
      selectedLocation,
    ],
    queryFn: ({ signal }) => {
      const location =
        switchEnergyConsumptionChartView ===
          EnergyConsumptionChartType.Energy_Vs_Sensor
          ? selectedLocation
          : null;
      const locationType =
        switchEnergyConsumptionChartView ===
          EnergyConsumptionChartType.Energy_Vs_Sensor
          ? assetDisplayLevelsByBlock?.find(
            (item: any) => item.DisplayLevel === selectedLocation
          )?.Type
          : null;
      return getEnergyConsumptionDataByBlockCode(
        blockCode,
        switchEnergyConsumptionChartView,
        location,
        locationType,
        signal
      );
    },
    enabled: !!blockCode || isRefreshed,
  });

  const checkAllValuesForNull = (arr: any[]) => {
    return arr?.every((value) => value === null) ? null : arr;
  };

  useEffect(() => {
    if (
      energyConsumptionData?.data?.length ||
      energyConsumptionData?.data?.assetDisplayLevels?.length ||
      energyConsumptionData?.data?.assetDisplayLocations?.length ||
      energyConsumptionData?.data?.xAxisValues
    ) {
      if (
        switchEnergyConsumptionChartView ===
        EnergyConsumptionChartType.Energy_Vs_Time
      ) {
        setEnergyConsumptionBaselineValues(
          energyConsumptionData?.data[0]?.energyConsumptionBaseline?.map(
            (val: any) => (val === 0 ? null : val)
          )
        );

        if (
          checkAllValuesForNull(
            energyConsumptionData?.data[0]?.energyConsumptionInWKH
          )
        ) {
          setEnergyConsumptionChartData(
            energyConsumptionData?.data[0]?.energyConsumptionInWKH
          );

          setSavedEnergy(energyConsumptionData?.data[0]?.savedEnergy);
        } else {
          setEnergyConsumptionChartData([]);
          setSavedEnergy([]);
        }
      } else if (
        switchEnergyConsumptionChartView ===
        EnergyConsumptionChartType.Energy_Vs_Level
      ) {
        const { seriesData, xAxisValues } = energyConsumptionData.data;

        setEnergyConsumptionVsLevelData({ seriesData, xAxisValues });
      } else if (
        switchEnergyConsumptionChartView ===
        EnergyConsumptionChartType.Energy_Vs_Location
      ) {
        const { seriesData, xAxisValues } = energyConsumptionData.data;
        setEnergyConsumptionVsLocationData({ seriesData, xAxisValues });
      } else {
        const consumedEnergy = energyConsumptionData?.data?.map(
          (item: any) => item?.energyConsuption
        );

        const sensorNode = energyConsumptionData?.data?.map(
          (item: any) => item?.assetDisplayName
        );

        const extraData = energyConsumptionData?.data?.map((d: any) => {
          return {
            code: d?.assetCode,
            displayName: d?.assetDisplayName,
            energySaved: d?.energySaved,
            energyConsuption: d?.energyConsuption,
            level: d?.level,
          };
        });

        setEnergyConsumptionVsSensorData({
          energyConsumed: consumedEnergy,
          sensorNode: sensorNode,
          extraData: extraData,
        });
      }
    }
  }, [energyConsumptionData]);

  useEffect(() => {
    if (activeKey === "Time") {
      setSwitchEnergyConsumptionChartView(
        EnergyConsumptionChartType.Energy_Vs_Time
      );
    } else if (activeKey === "Level") {
      setSwitchEnergyConsumptionChartView(
        EnergyConsumptionChartType.Energy_Vs_Level
      );
    } else if (activeKey === "Location") {
      setSwitchEnergyConsumptionChartView(
        EnergyConsumptionChartType.Energy_Vs_Location
      );
    } else {
      setSwitchEnergyConsumptionChartView(
        EnergyConsumptionChartType.Energy_Vs_Sensor
      );
    }

    setRender(render + 1);
  }, [activeKey, switchEnergyConsumptionChartView]);

  const onChangeHandler = (selectedValue: any) => {
    const newValue = selectedValue === "All Level" ? null : selectedValue;

    // If there's no change, exit early
    if (newValue === selectedLocation) return;

    // Reset the data when changing the location
    setEnergyConsumptionVsSensorData({
      energyConsumed: [],
      sensorNode: [],
      extraData: [],
    });

    // Update selection
    setSelectedLocation(newValue);
    setRender(render + 1);
  };

  return (
    <div className="w-lg-48 w-100 mt-4">
      <div className="bg-card">
        <div>
          <h4 className="h4" style={{ marginBottom: "1rem" }}>
            Energy Consumption{" "}
            <span style={{ fontSize: "16px" }}>
              {activeKey === "Time" ? " (Last 7 days)" : " (Last Day)"}{blockDetails?.slsSupplier === "ST Engineering" && <ChipComponent label="Estimated" />}
            </span>
          </h4>
        </div>

        <div className="custom_tab motion-traffic-responsive-tabs">
          <B2Tabs
            key={render}
            activeKey={activeKey}
            pills
            onSelect={(selectedId: any) => {
              if (selectedId === "Time") {
                setSwitchEnergyConsumptionChartView(
                  EnergyConsumptionChartType.Energy_Vs_Time
                );

                setActiveKey("Time");
              } else if (selectedId === "Level") {
                setSwitchEnergyConsumptionChartView(
                  EnergyConsumptionChartType.Energy_Vs_Level
                );

                setActiveKey("Level");
              } else if (selectedId === "Location") {
                setSwitchEnergyConsumptionChartView(
                  EnergyConsumptionChartType.Energy_Vs_Location
                );

                setActiveKey("Location");
              } else {
                setSwitchEnergyConsumptionChartView(
                  EnergyConsumptionChartType.Energy_Vs_Sensor
                );

                setSelectedLocation(null);
                setActiveKey("Sensor");
              }
            }}
            tab={[
              {
                content: (
                  <CustomSpinnerComponent
                    height={"400px"}
                    width={"100%"}
                    isLoading={isLoadingEnergyConsumptionData || isRefreshed}
                  >
                    <CustomSplineChart
                      xAxisTitleText="Time (Date)"
                      yAxisTitleText="Energy Consumption(Wh)"
                      seriesData={energyConsumptionChartData}
                      baseline={energyConsumptionBaselineValues}
                      savedEnergy={savedEnergy}
                      fileName={generateFilenameWithTimestamp(
                        "Energy Consumption(last 7 days)"
                      )}
                      chartName={"Energy Consumption(last 7 days)"}
                    />
                  </CustomSpinnerComponent>
                ),
                id: "Time",
                title: "Energy vs Time",
              },
              {
                content: (
                  <CustomSpinnerComponent
                    height={"400px"}
                    width={"100%"}
                    isLoading={isLoadingEnergyConsumptionData || isRefreshed}
                  >
                    <EnergyStackedChartComponent
                      xAxisCategories={
                        energyConsumptionVsLevelData?.xAxisValues
                      }
                      SeriesType={"column"}
                      seriesData={energyConsumptionVsLevelData?.seriesData}
                      seriesShowInLegend={false}
                      isShowDataLevels={false}
                      isShowStackedColor={false}
                      seriesName={"Energy Consumption(Wh)"}
                      xAxisTitleText={"Level"}
                      yAxisTitleText={"Energy Consumption(Wh)"}
                      tooltipPrefix={"Level: "}
                      setSelectedLocation={setSelectedLocation}
                      setActiveKey={setActiveKey}
                    />
                  </CustomSpinnerComponent>
                ),
                id: "Level",
                title: "Energy vs Level",
              },
              {
                content: (
                  <CustomSpinnerComponent
                    height={"400px"}
                    width={"100%"}
                    isLoading={isLoadingEnergyConsumptionData || isRefreshed}
                  >
                    <EnergyStackedChartComponent
                      xAxisCategories={
                        energyConsumptionVsLocationData?.xAxisValues
                      }
                      SeriesType={"column"}
                      seriesData={energyConsumptionVsLocationData?.seriesData}
                      seriesShowInLegend={false}
                      isShowDataLevels={false}
                      isShowStackedColor={false}
                      seriesName={"Energy Consumption(Wh)"}
                      xAxisTitleText={"Location"}
                      yAxisTitleText={"Energy Consumption(Wh)"}
                      tooltipPrefix={"Location: "}
                      setSelectedLocation={setSelectedLocation}
                      setActiveKey={setActiveKey}
                    />
                  </CustomSpinnerComponent>
                ),
                id: "Location",
                title: "Energy vs Location",
              },
              {
                content: (
                  <CustomSpinnerComponent
                    height={"400px"}
                    width={"100%"}
                    isLoading={isLoadingEnergyConsumptionData || isRefreshed}
                  >
                    <TabularFilterComponent
                      selectedValue={selectedLocation}
                      options={assetDisplayLevelsByBlock}
                      onChangeHandler={onChangeHandler}
                      placeHolder={responsivePlaceholder}
                      style={tabularFilterStyle}
                    />
                    <CustomHighchartComponent
                      xAxisCategories={
                        energyConsumptionVsSensorData?.sensorNode
                      }
                      SeriesType={"column"}
                      seriesData={energyConsumptionVsSensorData?.energyConsumed}
                      seriesShowInLegend={false}
                      seriesName={"Energy Consumption(Wh)"}
                      xAxisTitleText={"Sensors"}
                      yAxisTitleText={"Energy Consumption(Wh)"}
                      colors={["#66b2ff"]}
                      extraData={energyConsumptionVsSensorData?.extraData}
                      redirectRoute="/asset/"
                      isEnergyTooltip={true}
                      setSelectedLocation={setSelectedLocation}
                    />
                  </CustomSpinnerComponent>
                ),
                id: "Sensor",
                title: "Energy vs Sensor",
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(EnergyConsumptionChart);
